import { TaskStatus, TriggerKindInfo, VariableGroup } from "client/openapi";
import { defaultOptions } from "components/MessageTemplates/TriggerDialogs";
import {
  columnByKey,
  kindInfo,
  ReferenceData,
  ROLE_OF_ACCOUNT_ROLE,
} from "./reference";
import type {
  AssignChoice,
  PersonRole,
  StepAction,
  Trigger,
  TriggerFilter,
} from "./types";

/*
 * The trigger kinds: the backend's (from the template catalog), then the
 * two the screens split the backend's "A task's status changes" into.
 * Nothing here is a recipe: these are the general pieces every automation
 * is built from.
 */

export const TASK_FINISHED = "task_finished";
export const TASK_STATUS_CHANGED = "task_status_changed";

/** A trigger kind, as the pickers offer it. */
export interface KindDef {
  kind: string;
  label: string;
  description: string;
  /** The backend's about (students, families, people, audience), or "tasks". */
  about: TriggerKindInfo.about | "tasks";
  /** In the template catalog: its choices are the trigger dialog's. False: a task kind. */
  inCatalog: boolean;
  promotional: boolean;
  /** The step actions it can start. */
  actions: StepAction[];
  /**
   * The person "From the event" means, in words ("The person who finished
   * the task"), or null when the event has nobody of its own.
   */
  eventPerson: string | null;
}

export const ALL_ACTIONS: StepAction[] = [
  "message",
  "task",
  "crm_move",
  "crm_field",
  "close_task",
  "assign_tutor",
  "add_note",
];

/** Each step action as the backend names it (its choices, steps, Activity rows and cards). */
export const BACKEND_ACTIONS: Record<StepAction, string> = {
  message: "send",
  task: "task",
  crm_move: "crm_move",
  crm_field: "crm_field",
  close_task: "task_close",
  assign_tutor: "tutor_assign",
  add_note: "note",
};

/** As the contract has them, for choices that don't say yet. */
const ACTION_DEFAULTS: Partial<
  Record<StepAction, { askFirst: boolean; people: PersonRole[] }>
> = {
  assign_tutor: { askFirst: true, people: ["student", "parent"] },
  add_note: { askFirst: false, people: ["student", "parent"] },
};

/**
 * What the backend's choices say of an action: a new step's Ask first
 * (on for Assign a tutor), and the kinds of person it is for whatever its
 * settings (empty: anyone, or as its settings say).
 */
export function actionInfo(
  ref: ReferenceData,
  action: StepAction
): { askFirst: boolean; people: PersonRole[] } {
  const info = ref.choices.actions?.find(
    (a) => a.action === BACKEND_ACTIONS[action]
  ) as { ask_first?: boolean; people?: string[] } | undefined;
  const fallback = ACTION_DEFAULTS[action];
  const people = (info?.people ?? fallback?.people ?? []).filter(
    (p): p is PersonRole => ["admin", "tutor", "parent", "student"].includes(p)
  );
  return { askFirst: info?.ask_first ?? fallback?.askFirst ?? false, people };
}

/**
 * The backend's "A task's status changes" (task_status), as two kinds:
 * a task is finished (its status is Complete), or its status changes (to
 * one status, or any).
 */
export const TASK_KINDS: KindDef[] = [
  {
    kind: TASK_FINISHED,
    label: "A task is finished",
    description: "When someone marks a task complete.",
    about: "tasks",
    inCatalog: false,
    promotional: false,
    actions: ALL_ACTIONS,
    eventPerson: "The person who finished the task",
  },
  {
    kind: TASK_STATUS_CHANGED,
    label: "A task's status changes",
    description: "When someone changes a task's status.",
    about: "tasks",
    inCatalog: false,
    promotional: false,
    actions: ALL_ACTIONS,
    eventPerson: "The person who changed its status",
  },
];

/**
 * The person "From the event" means for a kind, in the backend's words:
 * whoever filled out the form, for a kind whose runs carry a form. A task
 * kind says its own.
 */
function eventPersonOf(ref: ReferenceData, kind: string) {
  if (!kindInfo(ref, kind)?.gives?.includes("form")) return null;
  return (
    ref.choices.assign_choices.find((c) => c.key === "filler")?.label ??
    "The person who filled out the form"
  );
}

/**
 * Every kind the pickers offer: the backend's, in its order, then the
 * task kinds. Every kind can start every action, On a schedule included
 * (Andrew's decision, 2026-10-28).
 */
export function kindsOf(ref: ReferenceData): KindDef[] {
  const backend: KindDef[] = ref.catalog.trigger_kinds.map((info) => ({
    kind: info.kind,
    label: info.label,
    description: info.description,
    about: info.about,
    inCatalog: true,
    promotional: !!info.promotional,
    actions: ALL_ACTIONS,
    eventPerson: eventPersonOf(ref, info.kind),
  }));
  const served = new Set(backend.map((k) => k.kind));
  return [...backend, ...TASK_KINDS.filter((k) => !served.has(k.kind))];
}

export function kindOf(ref: ReferenceData, kind: string): KindDef | null {
  return kindsOf(ref).find((k) => k.kind === kind) ?? null;
}

/**
 * The kinds as a picker offers them. boxed: for a card in an AND box of two
 * or more, where a kind that says where someone is goes by its own words
 * (the backend's box_label, "In a CRM column"). Its stored kind stays.
 */
export function pickerKinds(ref: ReferenceData, boxed = false): KindDef[] {
  const kinds = kindsOf(ref);
  if (!boxed) return kinds;
  return kinds.map((k) => {
    const info = kindInfo(ref, k.kind);
    return info?.box_label
      ? {
          ...k,
          label: info.box_label,
          description: info.box_description ?? k.description,
        }
      : k;
  });
}

export function isTaskKind(kind: string) {
  return kind === TASK_FINISHED || kind === TASK_STATUS_CHANGED;
}

/** The statuses a task can have, in the Tasks page's words. */
export const TASK_STATUSES: { value: TaskStatus; label: string }[] =
  Object.values(TaskStatus).map((status) => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
  }));

export function statusLabel(status: TaskStatus | string) {
  return TASK_STATUSES.find((s) => s.value === status)?.label ?? String(status);
}

/**
 * A new trigger of this kind, with the kind's own defaults. A kind's own
 * timing ("3 days after") is not a trigger option: its first steps carry it
 * (see kindTiming).
 */
export function defaultTrigger(ref: ReferenceData, kind: string): Trigger {
  if (isTaskKind(kind)) {
    const filters: TriggerFilter[] = [{ field: "name_contains", value: "" }];
    return {
      kind,
      options: kind === TASK_STATUS_CHANGED ? { to: "any" } : {},
      filters,
    };
  }
  const options = defaultOptions(kind, ref.catalog);
  const timing = kindTiming(ref, kind);
  if (timing) delete options[timing.key];
  return { kind, options, filters: [] };
}

// ── A kind's own timing ───────────────────────────────────────────────────

/**
 * When a kind's first steps can happen, measured from its own moment (the
 * first meeting, the form filled out, a meeting's start, a date): the
 * option the backend keeps it in (its unit), which side of that moment,
 * its limits in that unit, and how "3 days ___" ends before and after it
 * (null after: "After 3 days").
 */
export interface KindTiming {
  key: "days" | "hours";
  sides: "after" | "before" | "both";
  min: number;
  max: number;
  before: string;
  after: string | null;
}

/** How "3 days ___" ends, where a kind's own words say more than "After 3 days". */
const TIMING_WORDS: Record<string, { before?: string; after?: string }> = {
  meeting_before: { before: "before" },
  meeting_after: { after: "after" },
  meeting_missed: { after: "after" },
  no_meeting: { after: "with no meeting" },
  form_waiting: { after: "after it was sent" },
  balance_owed: { after: "owed" },
  no_payment_method: { after: "with no card" },
  crm_date: { before: "before", after: "after" },
};

/** The kind's own timing (the backend's choices), or null for a kind whose steps time themselves. */
export function kindTiming(
  ref: ReferenceData,
  kind: string
): KindTiming | null {
  const timing = kindInfo(ref, kind)?.timing;
  if (!timing) return null;
  return {
    key: timing.unit === "hours" ? "hours" : "days",
    sides: timing.sign < 0 ? "before" : timing.low < 0 ? "both" : "after",
    min: timing.low,
    max: timing.high,
    before: TIMING_WORDS[kind]?.before ?? "before",
    after: TIMING_WORDS[kind]?.after ?? null,
  };
}

/** The kind's default timing, as the trigger dialog starts it (signed: negative is before). */
export function defaultTimingValue(ref: ReferenceData, kind: string): number {
  const timing = kindTiming(ref, kind);
  if (!timing) return 0;
  const value = Number(defaultOptions(kind, ref.catalog)[timing.key]) || 0;
  return timing.sides === "before" ? -Math.abs(value) : value;
}

/**
 * Some kinds' timing changes only while an automation is off: one that
 * keys what it sends by the day it acts ("Around a date"), as the
 * backend's choices say, so a new time can't send it again.
 */
export function timingLockedWhileOn(ref: ReferenceData, kind: string) {
  return kindInfo(ref, kind)?.timing?.changes_while_on === false;
}

// ── Who an automation is about ────────────────────────────────────────────

/** What a trigger's runs can be about, which decides the choices that apply. */
export interface Facets {
  /** The kinds of people it can be about. */
  roles: PersonRole[];
  /** A task kind: the task is part of the event. */
  task: boolean;
  /** The event's own person, in words, if it has one. */
  eventPerson: string | null;
}

/**
 * The kinds of people a trigger is about, from its kind and its choices
 * (a column holds one kind of person, a tutors' form is filled out by
 * tutors, a date field belongs to one kind).
 */
export function aboutRoles(ref: ReferenceData, trigger: Trigger): PersonRole[] {
  const o = trigger.options || {};
  if (isTaskKind(trigger.kind)) return ["student", "parent", "tutor"];
  const info = ref.catalog.trigger_kinds.find((k) => k.kind === trigger.kind);
  const roleOfColumn = (key: unknown): PersonRole[] => {
    const found = columnByKey(ref, typeof key === "string" ? key : null);
    if (found) return [found.column.role];
    const choice = ref.catalog.columns.find((c) => c.value === key);
    const role = choice?.person_type
      ? ROLE_OF_ACCOUNT_ROLE[choice.person_type]
      : null;
    return role ? [role] : ["student"];
  };
  switch (info?.about) {
    case TriggerKindInfo.about.AUDIENCE:
      if (o.audience === "parents") return ["parent"];
      if (o.audience === "tutors") return ["tutor"];
      if (o.audience === "column") return roleOfColumn(o.column);
      return ["student"];
    case TriggerKindInfo.about.STUDENTS:
      return ["student"];
    case TriggerKindInfo.about.FAMILIES:
      return ["student", "parent"];
    default: {
      if ("column" in o) return roleOfColumn(o.column);
      if ("form_id" in o) {
        const form = ref.catalog.forms.find(
          (f) => f.value === String(o.form_id)
        );
        const role = form?.person_type
          ? ROLE_OF_ACCOUNT_ROLE[form.person_type]
          : null;
        return role === "tutor" ? ["tutor"] : ["student", "parent"];
      }
      if ("field" in o) {
        const field = ref.catalog.date_fields.find((f) => f.value === o.field);
        const role = field?.person_type
          ? ROLE_OF_ACCOUNT_ROLE[field.person_type]
          : null;
        return role ? [role] : ["student"];
      }
      return ["student", "parent"];
    }
  }
}

export function facetsOf(ref: ReferenceData, trigger: Trigger): Facets {
  return {
    roles: aboutRoles(ref, trigger),
    task: isTaskKind(trigger.kind),
    eventPerson: kindOf(ref, trigger.kind)?.eventPerson ?? null,
  };
}

/** The step actions a trigger can start. */
export function actionsFor(ref: ReferenceData, trigger: Trigger): StepAction[] {
  return kindOf(ref, trigger.kind)?.actions ?? ALL_ACTIONS;
}

// ── Assign to ─────────────────────────────────────────────────────────────

export interface AssignChoiceDef {
  key: AssignChoice;
  label: (facets: Facets) => string;
  applies: (facets: Facets) => boolean;
  /** Said instead of the example when it can't apply here. */
  note: string;
}

export const ASSIGN_CHOICES: Record<AssignChoice, AssignChoiceDef> = {
  // The backend's student (the tutor or parent when it is only about them).
  self: {
    key: "self",
    label: (f) =>
      f.roles.length > 1 ? "The student" : "The person it is about",
    applies: (f) => f.roles.length > 0,
    note: "When it is about someone",
  },
  parents: {
    key: "parents",
    label: () => "Their parents",
    applies: (f) => f.roles.includes("student"),
    note: "When it is about a student",
  },
  tutors: {
    key: "tutors",
    label: () => "Their tutors",
    applies: (f) => f.roles.includes("student") || f.roles.includes("parent"),
    note: "When it is about a student or a family",
  },
  event_person: {
    key: "event_person",
    label: (f) => f.eventPerson ?? "The person from the event",
    applies: (f) => !!f.eventPerson,
    note: "For task and form events",
  },
  task_assignees: {
    key: "task_assignees",
    label: () => "That task's assignees",
    applies: (f) => f.task,
    note: "For task events",
  },
  admins: {
    key: "admins",
    label: () => "All admins",
    applies: () => true,
    note: "",
  },
  all_tutors: {
    key: "all_tutors",
    label: () => "All tutors",
    applies: () => true,
    note: "",
  },
};

/** The Assign to choices in their groups, in the order the picker shows them. */
export const ASSIGN_GROUPS: {
  key: string;
  label: string;
  choices: AssignChoice[];
}[] = [
  {
    key: "about",
    label: "Related to who it is about",
    choices: ["self", "parents", "tutors"],
  },
  {
    key: "event",
    label: "From the event",
    choices: ["event_person", "task_assignees"],
  },
  { key: "role", label: "By role", choices: ["admins", "all_tutors"] },
];

export const SHARE_MODES: { value: "all" | "each" | "turns"; label: string }[] =
  [
    { value: "all", label: "One task for all" },
    { value: "each", label: "One task each" },
    { value: "turns", label: "Take turns" },
  ];

// ── Variables ─────────────────────────────────────────────────────────────

/** Whether every key of a group starts this way: "This task"'s task.*, which only a task kind fills in, or "Admin"'s admin.*, which only a message to the admins does. */
function isGroupOf(group: VariableGroup, start: string) {
  return (
    group.variables.length > 0 &&
    group.variables.every((v) => v.key.startsWith(start))
  );
}

/** The variables a step's own wording (a task's, a note's) can use under this trigger (the backend's own groups). */
export function variablesFor(
  ref: ReferenceData,
  trigger: Trigger
): VariableGroup[] {
  const groups = ref.catalog.variables.filter(
    (group) => !isGroupOf(group, "admin.")
  );
  return isTaskKind(trigger.kind)
    ? groups
    : groups.filter((group) => !isGroupOf(group, "task."));
}
